import { CheckCircle2, Clock, Play, XCircle, AlertCircle } from 'lucide-react';
import type { JobTask, JobTaskStatus } from '@maker-toolchest/shared';

interface TasksSummaryProps {
  tasks: JobTask[];
  onStatusClick?: (status: JobTaskStatus) => void;
}

export function TasksSummary({ tasks, onStatusClick }: TasksSummaryProps) {
  // Calculate status counts
  const statusCounts = tasks.reduce(
    (acc, task) => {
      acc[task.status] = (acc[task.status] || 0) + 1;
      return acc;
    },
    {} as Record<JobTaskStatus, number>
  );

  const totalTasks = tasks.length;
  const completedTasks = statusCounts.COMPLETED || 0;
  const inProgressTasks = statusCounts.IN_PROGRESS || 0;
  const pendingTasks = statusCounts.PENDING || 0;
  const cancelledTasks = statusCounts.CANCELLED || 0;
  const failedTasks = statusCounts.FAILED || 0;

  const statusCards = [
    {
      status: 'COMPLETED' as JobTaskStatus,
      label: 'Completed',
      count: completedTasks,
      icon: CheckCircle2,
      color: 'text-success',
      bgColor: 'bg-success/10',
      hoverBgColor: 'hover:bg-success/20',
    },
    {
      status: 'IN_PROGRESS' as JobTaskStatus,
      label: 'In Progress',
      count: inProgressTasks,
      icon: Play,
      color: 'text-info',
      bgColor: 'bg-info/10',
      hoverBgColor: 'hover:bg-info/20',
    },
    {
      status: 'PENDING' as JobTaskStatus,
      label: 'Pending',
      count: pendingTasks,
      icon: Clock,
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      hoverBgColor: 'hover:bg-warning/20',
    },
    {
      status: 'FAILED' as JobTaskStatus,
      label: 'Failed',
      count: failedTasks,
      icon: AlertCircle,
      color: 'text-error',
      bgColor: 'bg-error/10',
      hoverBgColor: 'hover:bg-error/20',
    },
    {
      status: 'CANCELLED' as JobTaskStatus,
      label: 'Cancelled',
      count: cancelledTasks,
      icon: XCircle,
      color: 'text-foreground-muted',
      bgColor: 'bg-background-subtle',
      hoverBgColor: 'hover:bg-background',
    },
  ];

  return (
    <div className="space-y-4">
      {/* Overall stats */}
      <div className="rounded-lg border border-border bg-background-subtle p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-foreground-muted">Total Tasks</div>
            <div className="mt-1 text-2xl font-semibold text-foreground">{totalTasks}</div>
          </div>
          <div className="text-right">
            <div className="text-sm text-foreground-muted">Completed</div>
            <div className="mt-1 text-2xl font-semibold text-foreground">
              {completedTasks} / {totalTasks}
            </div>
          </div>
        </div>
      </div>

      {/* Status breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {statusCards.map((statusCard) => {
          const isClickable = onStatusClick && statusCard.count > 0;
          const Component = isClickable ? 'button' : 'div';

          return (
            <Component
              key={statusCard.label}
              type={isClickable ? 'button' : undefined}
              onClick={isClickable ? () => onStatusClick(statusCard.status) : undefined}
              className={`
                rounded-lg border border-border p-4 ${statusCard.bgColor}
                ${isClickable ? `${statusCard.hoverBgColor} cursor-pointer transition-colors` : ''}
                ${isClickable && statusCard.count === 0 ? 'opacity-50' : ''}
              `}
              disabled={isClickable && statusCard.count === 0}
            >
              <div className="flex items-center gap-3">
                <statusCard.icon className={`h-5 w-5 flex-shrink-0 ${statusCard.color}`} />
                <div className="flex-1 min-w-0 text-left">
                  <div className="text-xs text-foreground-muted truncate">{statusCard.label}</div>
                  <div className={`mt-0.5 text-lg font-semibold ${statusCard.color}`}>
                    {statusCard.count}
                  </div>
                </div>
              </div>
            </Component>
          );
        })}
      </div>

      {totalTasks === 0 && (
        <div className="rounded-lg border border-border border-dashed bg-background-subtle p-8 text-center">
          <Clock className="h-12 w-12 mx-auto text-foreground-muted opacity-50" />
          <p className="mt-2 text-sm text-foreground-muted">No tasks yet</p>
        </div>
      )}
    </div>
  );
}
